import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sliders, Save, Eye, RotateCcw, Send, Loader2,
  Layout, Layers, Phone, Share2, Palette, LineChart, Code2,
  Plus, Trash2, ArrowUp, ArrowDown, AlertCircle, CheckCircle2,
  Instagram, Twitter, Facebook, Youtube, Linkedin, Music2,
  LayoutTemplate,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { websiteAPI } from '../../services/api';
import useDraftPublish from '../../hooks/useDraftPublish';
import { useTenantBranding } from '../../context/TenantBrandingContext';

// =====================================================================
// Helpers
// =====================================================================

const SECTION_KEYS = [
  'header', 'footer', 'contact', 'social', 'theme', 'analytics', 'custom',
];

const FONT_OPTIONS = [
  { value: 'Tajawal', label: 'Tajawal' },
  { value: 'Cairo', label: 'Cairo' },
  { value: 'system-ui', label: 'System UI' },
];

const ROUNDEDNESS_OPTIONS = [
  { value: 'sm', label: 'صغيرة (sm)' },
  { value: 'md', label: 'متوسطة (md)' },
  { value: 'lg', label: 'كبيرة (lg)' },
  { value: 'xl', label: 'كبيرة جداً (xl)' },
];

const ANALYTICS_REGEX = /^[A-Z0-9\-_]{1,50}$/i;

const emptyDefaults = {
  header: { showBookButton: true, menuItems: [] },
  footer: {
    about: { ar: '', en: '' },
    quickLinks: [],
    copyright: { ar: '', en: '' },
    workingHours: { ar: '', en: '' },
  },
  contact: {
    phone: '', whatsapp: '', email: '',
    address: { ar: '', en: '' },
    mapEmbedUrl: '', mapLat: '', mapLng: '',
  },
  social: {
    instagram: '', twitter: '', facebook: '',
    snapchat: '', tiktok: '', youtube: '', linkedin: '',
  },
  theme: {
    primaryColor: '#2563EB',
    secondaryColor: '#64748B',
    accentColor: '#10B981',
    fontFamily: 'Tajawal',
    roundedness: 'lg',
    darkLogoOnHero: false,
  },
  analytics: {
    gtmId: '', metaPixelId: '', googleAnalyticsId: '',
    tiktokPixelId: '', snapPixelId: '',
  },
  custom: { headHtml: '', bodyEndHtml: '' },
};

function deepMerge(base, patch) {
  if (Array.isArray(patch)) return patch;
  if (patch && typeof patch === 'object') {
    const out = { ...(base && typeof base === 'object' ? base : {}) };
    for (const k of Object.keys(patch)) {
      out[k] = deepMerge(out[k], patch[k]);
    }
    return out;
  }
  return patch;
}

function pickInitialSection(snapshot, key) {
  const fromSnap = snapshot?.[key];
  const baseline = emptyDefaults[key];
  if (!fromSnap) return JSON.parse(JSON.stringify(baseline));
  return deepMerge(JSON.parse(JSON.stringify(baseline)), fromSnap);
}

function shallowEqualJSON(a, b) {
  try { return JSON.stringify(a) === JSON.stringify(b); } catch (_) { return false; }
}

function formatRelativeAr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'قبل لحظات';
  if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
  if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
  if (diff < 604800) return `قبل ${Math.floor(diff / 86400)} يوم`;
  return d.toLocaleDateString('ar-SA-u-ca-gregory');
}

// =====================================================================
// Small reusable bits
// =====================================================================

const SectionCard = ({ icon: Icon, title, subtitle, dirty, saving, onSave, children, accent = 'blue' }) => {
  const accentMap = {
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    violet: 'bg-violet-100 text-violet-600',
    amber: 'bg-amber-100 text-amber-600',
    rose: 'bg-blue-100 text-blue-600',
    slate: 'bg-slate-100 text-slate-600',
    pink: 'bg-blue-100 text-blue-600',
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${accentMap[accent] || accentMap.blue}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-800">{title}</h3>
            {subtitle ? <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p> : null}
          </div>
        </div>
        <button
          onClick={onSave}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          حفظ هذا القسم
        </button>
      </div>
      <div className="p-5">{children}</div>
    </motion.div>
  );
};

const TextField = ({ label, value, onChange, placeholder, dir, type = 'text', hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      dir={dir}
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
    />
    {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
  </div>
);

const TextArea = ({ label, value, onChange, placeholder, dir, rows = 3, mono, hint }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <textarea
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      dir={dir}
      rows={rows}
      className={`w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm ${mono ? 'font-mono' : ''}`}
    />
    {hint ? <p className="text-xs text-gray-400 mt-1">{hint}</p> : null}
  </div>
);

const Toggle = ({ label, value, onChange, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={!!value}
      onChange={(e) => onChange(e.target.checked)}
      className="w-5 h-5 text-blue-600 rounded mt-0.5"
    />
    <div>
      <span className="font-medium text-gray-800 text-sm">{label}</span>
      {hint ? <p className="text-xs text-gray-500 mt-0.5">{hint}</p> : null}
    </div>
  </label>
);

// =====================================================================
// Main page
// =====================================================================

const SiteSettings = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantBranding();
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [changingTemplate, setChangingTemplate] = useState(false);

  const {
    state, loading, saving, publishing, reverting, previewLoading,
    saveDraft, publish, revert, openPreview,
  } = useDraftPublish({
    getState: websiteAPI.getSiteSettings,
    saveDraft: websiteAPI.saveSiteSettingsDraft,
    publish: websiteAPI.publishSiteSettings,
    revert: websiteAPI.revertSiteSettings,
    previewToken: websiteAPI.siteSettingsPreviewToken,
  });

  // Resolve the currently-selected template so we can display its name
  // next to the "Change Template" button.
  useEffect(() => {
    let cancelled = false;
    const templateId = tenant?.publicSite?.templateId;
    if (!templateId) { setCurrentTemplate(null); return undefined; }
    (async () => {
      try {
        const resp = await websiteAPI.listTemplates();
        const list = resp.data?.data?.templates || [];
        const match = list.find((t) => String(t._id) === String(templateId));
        if (!cancelled) setCurrentTemplate(match || null);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [tenant?.publicSite?.templateId]);

  const onChangeTemplate = useCallback(() => {
    const ok = window.confirm(
      'تغيير القالب قد يؤدي إلى فقدان بعض التخصيصات التي لا تتطابق مع القالب الجديد. هل أنت متأكد؟'
    );
    if (!ok) return;
    setChangingTemplate(true);
    navigate('/settings/website?force=gallery');
  }, [navigate]);

  const draft = state?.draftSnapshot || null;

  // Per-section local edit state
  const [sections, setSections] = useState(() => {
    const s = {};
    for (const k of SECTION_KEYS) s[k] = pickInitialSection(null, k);
    return s;
  });

  // Sync local editor state with draft whenever state arrives/changes.
  useEffect(() => {
    if (!draft) return;
    setSections((prev) => {
      const next = { ...prev };
      for (const k of SECTION_KEYS) {
        next[k] = pickInitialSection(draft, k);
      }
      return next;
    });
  }, [draft]);

  const dirtyMap = useMemo(() => {
    const out = {};
    for (const k of SECTION_KEYS) {
      const serverVal = pickInitialSection(draft, k);
      out[k] = !shallowEqualJSON(sections[k], serverVal);
    }
    return out;
  }, [sections, draft]);

  const anyDirty = SECTION_KEYS.some((k) => dirtyMap[k]);

  const updateSection = useCallback((key, updater) => {
    setSections((prev) => ({
      ...prev,
      [key]: typeof updater === 'function' ? updater(prev[key]) : updater,
    }));
  }, []);

  const saveSection = useCallback(async (key) => {
    if (!dirtyMap[key]) return;
    await saveDraft({ [key]: sections[key] });
  }, [dirtyMap, sections, saveDraft]);

  const saveAllDirty = useCallback(async () => {
    const partial = {};
    let count = 0;
    for (const k of SECTION_KEYS) {
      if (dirtyMap[k]) { partial[k] = sections[k]; count += 1; }
    }
    if (count === 0) return;
    await saveDraft(partial);
  }, [dirtyMap, sections, saveDraft]);

  // Validation — show soft errors for analytics IDs before user hits save
  const analyticsErrors = useMemo(() => {
    const a = sections.analytics || {};
    const errs = {};
    for (const [k, v] of Object.entries(a)) {
      if (v && !ANALYTICS_REGEX.test(v)) {
        errs[k] = 'يُسمح بالحروف والأرقام و - _ فقط (حتى 50 خانة)';
      }
    }
    return errs;
  }, [sections.analytics]);

  const hasAnalyticsErrors = Object.keys(analyticsErrors).length > 0;

  const onPublish = useCallback(async () => {
    if (hasAnalyticsErrors) {
      toast.error('يوجد معرفات تحليلات غير صالحة — صحّحها قبل النشر');
      return;
    }
    await publish();
  }, [publish, hasAnalyticsErrors]);

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center" dir="rtl">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  const hasUnpublished = !!state?.hasUnpublishedChanges;
  const isPublished = state?.status === 'published';
  const hasPublishedSnapshot = !!state?.publishedSnapshot;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" dir="rtl">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 mb-5"
        >
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-sm">
            <Sliders className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#1E293B] tracking-tight">إعدادات الموقع</h1>
            <p className="text-slate-500 text-sm mt-1">الهيدر والفوتر والهوية البصرية وأدوات التحليل</p>
          </div>
        </motion.div>

        {/* Status bar (sticky) */}
        <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#F8FAFC]/80 backdrop-blur border-b border-gray-100 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Status badge */}
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
                hasUnpublished
                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                  : isPublished
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-gray-50 text-gray-600 border border-gray-200'
              }`}
            >
              {hasUnpublished ? (
                <>
                  <AlertCircle className="w-3.5 h-3.5" />
                  تغييرات غير منشورة
                </>
              ) : isPublished ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  منشور
                </>
              ) : (
                <>
                  <AlertCircle className="w-3.5 h-3.5" />
                  مسودة فقط
                </>
              )}
            </span>

            {/* Timestamps */}
            <span className="text-xs text-gray-500">
              آخر حفظ: {formatRelativeAr(state?.draftUpdatedAt) || '—'}
            </span>
            {hasPublishedSnapshot && (
              <span className="text-xs text-gray-400 hidden sm:inline">
                • آخر نشر: {formatRelativeAr(state?.publishedAt) || '—'}
              </span>
            )}

            <div className="flex-1" />

            {/* Actions */}
            <button
              onClick={openPreview}
              disabled={previewLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              معاينة
            </button>

            <button
              onClick={saveAllDirty}
              disabled={!anyDirty || saving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-blue-50 border border-blue-100 text-blue-700 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              تحديث الكل
            </button>

            <button
              onClick={revert}
              disabled={!hasUnpublished || reverting}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {reverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              تجاهل التغييرات
            </button>

            <button
              onClick={onPublish}
              disabled={!hasUnpublished || publishing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              نشر
            </button>
          </div>
        </div>

        {/* Current template card */}
        {currentTemplate && (
          <div className="mb-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <LayoutTemplate className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-slate-400 mb-0.5">القالب الحالي</div>
              <div className="text-sm font-bold text-slate-900 truncate">
                {currentTemplate.name?.ar}
                <span className="mx-1 text-xs text-slate-400 font-normal">/ {currentTemplate.name?.en}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={onChangeTemplate}
              disabled={changingTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 disabled:opacity-60"
            >
              {changingTemplate ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LayoutTemplate className="w-3.5 h-3.5" />}
              تغيير القالب
            </button>
          </div>
        )}

        {/* Cards grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* HEADER */}
          <SectionCard
            icon={Layout}
            title="الهيدر"
            subtitle="القوائم العلوية وزر حجز الموعد"
            dirty={dirtyMap.header}
            saving={saving}
            accent="blue"
            onSave={() => saveSection('header')}
          >
            <div className="space-y-4">
              <Toggle
                label="إظهار زر حجز الموعد"
                hint="يظهر زر بارز في الهيدر يوجّه للعميل لحجز فحص"
                value={sections.header.showBookButton}
                onChange={(v) => updateSection('header', (s) => ({ ...s, showBookButton: v }))}
              />

              <ListEditor
                label="عناصر القائمة"
                emptyText="لا توجد قوائم بعد — أضف أول قائمة"
                items={sections.header.menuItems}
                onChange={(items) => updateSection('header', (s) => ({ ...s, menuItems: items }))}
                newItem={() => ({
                  labelAr: '',
                  labelEn: '',
                  href: '/',
                  order: sections.header.menuItems.length,
                  target: '_self',
                })}
                renderRow={(item, update) => (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input
                      value={item.labelAr || ''}
                      onChange={(e) => update({ ...item, labelAr: e.target.value })}
                      placeholder="العنوان (عربي)"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <input
                      value={item.labelEn || ''}
                      onChange={(e) => update({ ...item, labelEn: e.target.value })}
                      placeholder="Label (EN)"
                      dir="ltr"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <input
                      value={item.href || ''}
                      onChange={(e) => update({ ...item, href: e.target.value })}
                      placeholder="/path"
                      dir="ltr"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <select
                      value={item.target || '_self'}
                      onChange={(e) => update({ ...item, target: e.target.value })}
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="_self">نفس الصفحة</option>
                      <option value="_blank">نافذة جديدة</option>
                    </select>
                  </div>
                )}
              />
            </div>
          </SectionCard>

          {/* FOOTER */}
          <SectionCard
            icon={Layers}
            title="الفوتر"
            subtitle="النص التعريفي والروابط السريعة وساعات العمل"
            dirty={dirtyMap.footer}
            saving={saving}
            accent="slate"
            onSave={() => saveSection('footer')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextArea
                  label="نبذة (عربي)"
                  value={sections.footer.about?.ar}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, about: { ...(s.about || {}), ar: v },
                  }))}
                />
                <TextArea
                  label="About (EN)"
                  dir="ltr"
                  value={sections.footer.about?.en}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, about: { ...(s.about || {}), en: v },
                  }))}
                />
              </div>

              <ListEditor
                label="روابط سريعة"
                emptyText="لا توجد روابط بعد"
                items={sections.footer.quickLinks}
                onChange={(items) => updateSection('footer', (s) => ({ ...s, quickLinks: items }))}
                newItem={() => ({ labelAr: '', labelEn: '', href: '/' })}
                renderRow={(item, update) => (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input
                      value={item.labelAr || ''}
                      onChange={(e) => update({ ...item, labelAr: e.target.value })}
                      placeholder="العنوان (عربي)"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <input
                      value={item.labelEn || ''}
                      onChange={(e) => update({ ...item, labelEn: e.target.value })}
                      placeholder="Label (EN)"
                      dir="ltr"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                    <input
                      value={item.href || ''}
                      onChange={(e) => update({ ...item, href: e.target.value })}
                      placeholder="/path"
                      dir="ltr"
                      className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="ساعات العمل (عربي)"
                  value={sections.footer.workingHours?.ar}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, workingHours: { ...(s.workingHours || {}), ar: v },
                  }))}
                  placeholder="السبت - الخميس: 8ص - 10م"
                />
                <TextField
                  label="Working Hours (EN)"
                  dir="ltr"
                  value={sections.footer.workingHours?.en}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, workingHours: { ...(s.workingHours || {}), en: v },
                  }))}
                  placeholder="Sat - Thu: 8AM - 10PM"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="حقوق النشر (عربي)"
                  value={sections.footer.copyright?.ar}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, copyright: { ...(s.copyright || {}), ar: v },
                  }))}
                  placeholder="© 2026 جميع الحقوق محفوظة"
                />
                <TextField
                  label="Copyright (EN)"
                  dir="ltr"
                  value={sections.footer.copyright?.en}
                  onChange={(v) => updateSection('footer', (s) => ({
                    ...s, copyright: { ...(s.copyright || {}), en: v },
                  }))}
                />
              </div>
            </div>
          </SectionCard>

          {/* CONTACT */}
          <SectionCard
            icon={Phone}
            title="معلومات التواصل"
            subtitle="هاتف وواتساب وبريد إلكتروني وعنوان وخريطة"
            dirty={dirtyMap.contact}
            saving={saving}
            accent="emerald"
            onSave={() => saveSection('contact')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TextField
                  label="الهاتف"
                  dir="ltr"
                  value={sections.contact.phone}
                  onChange={(v) => updateSection('contact', (s) => ({ ...s, phone: v }))}
                  placeholder="+9665XXXXXXXX"
                  hint="ضع الرقم بالصيغة الدولية"
                />
                <TextField
                  label="واتساب"
                  dir="ltr"
                  value={sections.contact.whatsapp}
                  onChange={(v) => updateSection('contact', (s) => ({ ...s, whatsapp: v }))}
                  placeholder="+9665XXXXXXXX"
                />
                <TextField
                  label="البريد الإلكتروني"
                  type="email"
                  dir="ltr"
                  value={sections.contact.email}
                  onChange={(v) => updateSection('contact', (s) => ({ ...s, email: v }))}
                  placeholder="info@mylab.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextArea
                  label="العنوان (عربي)"
                  rows={2}
                  value={sections.contact.address?.ar}
                  onChange={(v) => updateSection('contact', (s) => ({
                    ...s, address: { ...(s.address || {}), ar: v },
                  }))}
                />
                <TextArea
                  label="Address (EN)"
                  dir="ltr"
                  rows={2}
                  value={sections.contact.address?.en}
                  onChange={(v) => updateSection('contact', (s) => ({
                    ...s, address: { ...(s.address || {}), en: v },
                  }))}
                />
              </div>

              <TextField
                label="رابط Google Maps Embed"
                dir="ltr"
                value={sections.contact.mapEmbedUrl}
                onChange={(v) => updateSection('contact', (s) => ({ ...s, mapEmbedUrl: v }))}
                placeholder="https://www.google.com/maps/embed?pb=..."
                hint="من Google Maps > مشاركة > تضمين خريطة"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="Latitude"
                  type="number"
                  dir="ltr"
                  value={sections.contact.mapLat}
                  onChange={(v) => updateSection('contact', (s) => ({ ...s, mapLat: v }))}
                  placeholder="24.7136"
                />
                <TextField
                  label="Longitude"
                  type="number"
                  dir="ltr"
                  value={sections.contact.mapLng}
                  onChange={(v) => updateSection('contact', (s) => ({ ...s, mapLng: v }))}
                  placeholder="46.6753"
                />
              </div>
            </div>
          </SectionCard>

          {/* SOCIAL */}
          <SectionCard
            icon={Share2}
            title="روابط التواصل الاجتماعي"
            subtitle="اترك الحقل فارغاً لإخفائه من الموقع"
            dirty={dirtyMap.social}
            saving={saving}
            accent="pink"
            onSave={() => saveSection('social')}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <SocialInput icon={Instagram} color="text-blue-500" label="Instagram" value={sections.social.instagram} onChange={(v) => updateSection('social', (s) => ({ ...s, instagram: v }))} />
              <SocialInput icon={Twitter} color="text-sky-500" label="Twitter / X" value={sections.social.twitter} onChange={(v) => updateSection('social', (s) => ({ ...s, twitter: v }))} />
              <SocialInput icon={Facebook} color="text-blue-600" label="Facebook" value={sections.social.facebook} onChange={(v) => updateSection('social', (s) => ({ ...s, facebook: v }))} />
              <SocialInput icon={Music2} color="text-gray-900" label="TikTok" value={sections.social.tiktok} onChange={(v) => updateSection('social', (s) => ({ ...s, tiktok: v }))} />
              <SocialInput icon={Youtube} color="text-red-600" label="YouTube" value={sections.social.youtube} onChange={(v) => updateSection('social', (s) => ({ ...s, youtube: v }))} />
              <SocialInput icon={Linkedin} color="text-blue-700" label="LinkedIn" value={sections.social.linkedin} onChange={(v) => updateSection('social', (s) => ({ ...s, linkedin: v }))} />
              <SocialInput icon={Share2} color="text-yellow-500" label="Snapchat" value={sections.social.snapchat} onChange={(v) => updateSection('social', (s) => ({ ...s, snapchat: v }))} />
            </div>
          </SectionCard>

          {/* THEME */}
          <SectionCard
            icon={Palette}
            title="الهوية البصرية"
            subtitle="الخط العام لموقعك (الألوان تُعدَّل من محرر القالب)"
            dirty={dirtyMap.theme}
            saving={saving}
            accent="violet"
            onSave={() => saveSection('theme')}
          >
            <div className="space-y-4">
              <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-[13px] text-blue-800 leading-relaxed">
                🎨 ألوان الموقع (الأساسي، الثانوي، المميّز) تُضبط الآن من
                <strong> محرر القالب </strong> مباشرة — التعديلات تنعكس فوراً
                على كل الصفحات. هذه الصفحة تتحكّم فقط في الإعدادات العامة
                (الخط، التدوير، التحليلات، إلخ).
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">الخط</label>
                  <select
                    value={sections.theme.fontFamily || 'Tajawal'}
                    onChange={(e) => updateSection('theme', (s) => ({ ...s, fontFamily: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-sm"
                  >
                    {FONT_OPTIONS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">درجة تدوير الحواف</label>
                  <select
                    value={sections.theme.roundedness || 'lg'}
                    onChange={(e) => updateSection('theme', (s) => ({ ...s, roundedness: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-sm"
                  >
                    {ROUNDEDNESS_OPTIONS.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Toggle
                label="شعار داكن على الهيرو"
                hint="فعّل هذا الخيار إن كانت صورة الغلاف فاتحة وبحاجة لشعار داكن"
                value={sections.theme.darkLogoOnHero}
                onChange={(v) => updateSection('theme', (s) => ({ ...s, darkLogoOnHero: v }))}
              />
            </div>
          </SectionCard>

          {/* ANALYTICS */}
          <SectionCard
            icon={LineChart}
            title="أدوات التحليل والتتبع"
            subtitle="GTM / Meta / GA4 / TikTok / Snapchat"
            dirty={dirtyMap.analytics}
            saving={saving}
            accent="amber"
            onSave={() => saveSection('analytics')}
          >
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: 'gtmId', label: 'Google Tag Manager ID', placeholder: 'GTM-XXXXXX' },
                  { key: 'googleAnalyticsId', label: 'Google Analytics 4 ID', placeholder: 'G-XXXXXXXXXX' },
                  { key: 'metaPixelId', label: 'Meta Pixel ID', placeholder: '1234567890' },
                  { key: 'tiktokPixelId', label: 'TikTok Pixel ID', placeholder: 'XXXXXXXXXXXXXXXXXXXX' },
                  { key: 'snapPixelId', label: 'Snap Pixel ID', placeholder: 'XXXXXXXX-XXXX-XXXX' },
                ].map((f) => (
                  <div key={f.key}>
                    <TextField
                      label={f.label}
                      dir="ltr"
                      placeholder={f.placeholder}
                      value={sections.analytics[f.key]}
                      onChange={(v) => updateSection('analytics', (s) => ({ ...s, [f.key]: v }))}
                    />
                    {analyticsErrors[f.key] ? (
                      <p className="text-xs text-blue-600 mt-1">{analyticsErrors[f.key]}</p>
                    ) : null}
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>المعرّفات تقبل الحروف والأرقام و - _ فقط، بحد أقصى 50 خانة. اتركها فارغة لتعطيل التتبع.</span>
              </div>
            </div>
          </SectionCard>

          {/* CUSTOM HTML */}
          <SectionCard
            icon={Code2}
            title="أكواد مخصّصة (Head / Body)"
            subtitle="للمستخدمين المتقدمين فقط"
            dirty={dirtyMap.custom}
            saving={saving}
            accent="rose"
            onSave={() => saveSection('custom')}
          >
            <div className="space-y-4">
              <TextArea
                label="Head HTML"
                dir="ltr"
                rows={5}
                mono
                placeholder="<!-- سيتم إدراجه داخل <head> -->"
                value={sections.custom.headHtml}
                onChange={(v) => updateSection('custom', (s) => ({ ...s, headHtml: v }))}
              />
              <TextArea
                label="Body End HTML"
                dir="ltr"
                rows={5}
                mono
                placeholder="<!-- سيتم إدراجه قبل </body> -->"
                value={sections.custom.bodyEndHtml}
                onChange={(v) => updateSection('custom', (s) => ({ ...s, bodyEndHtml: v }))}
              />
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  الأكواد هنا تُحقن كما هي في موقعك العام. تأكّد من أن المصدر موثوق —
                  أي سكربت ضار قد يُسبب مخاطر أمنية (XSS) على زوار موقعك.
                </span>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Sub components
// =====================================================================

const ListEditor = ({ label, items = [], onChange, newItem, renderRow, emptyText }) => {
  const swap = (i, j) => {
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    // re-assign order if present
    next.forEach((it, idx) => { if (Object.prototype.hasOwnProperty.call(it, 'order')) it.order = idx; });
    onChange(next);
  };
  const remove = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    next.forEach((it, idx) => { if (Object.prototype.hasOwnProperty.call(it, 'order')) it.order = idx; });
    onChange(next);
  };
  const update = (i, updated) => {
    const next = [...items];
    next[i] = updated;
    onChange(next);
  };
  const add = () => onChange([...items, newItem()]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <button
          onClick={add}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
        >
          <Plus className="w-3.5 h-3.5" /> إضافة
        </button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
          {emptyText}
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={idx} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => swap(idx, idx - 1)}
                    disabled={idx === 0}
                    className="p-1 rounded hover:bg-white text-gray-500 disabled:opacity-30"
                    title="نقل لأعلى"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => swap(idx, idx + 1)}
                    disabled={idx === items.length - 1}
                    className="p-1 rounded hover:bg-white text-gray-500 disabled:opacity-30"
                    title="نقل لأسفل"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1">
                  {renderRow(item, (updated) => update(idx, updated))}
                </div>
                <button
                  onClick={() => remove(idx)}
                  className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50"
                  title="حذف"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SocialInput = ({ icon: Icon, color, label, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color}`} />
      {label}
    </label>
    <input
      type="url"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="https://..."
      dir="ltr"
      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm"
    />
  </div>
);

export default SiteSettings;
